import type { Metric } from "web-vitals";

/* Forwards Core Web Vitals to the self-hosted analytics (Umami) as events.
 * See docs/plans/todo/self-hosted-usage-analytics.md (web-vitals hook).
 * - Rides the tracker injected by public/app-boot.js: when the tracker is
 *   absent (local dev / CI — no REACT_APP_UMAMI_* config), nothing is sent.
 * - The tracker's before-send normalizer scrubs the page URL attached to
 *   events, so D-Norm (no resource IDs) applies to these events too.
 * - umami.track() does NOT honor localStorage['umami.disabled']
 *   (umami-software/umami#3031), so the opt-out is enforced here explicitly.
 * - web-vitals v2 has no `rating` field; thresholds are Google's documented
 *   good / needs-improvement boundaries per metric. */

const THRESHOLDS: Record<string, [number, number]> = {
  CLS: [0.1, 0.25],
  FID: [100, 300],
  FCP: [1800, 3000],
  LCP: [2500, 4000],
  TTFB: [800, 1800],
};

type UmamiWindow = Window & {
  umami?: { track?: (event: string, data?: Record<string, unknown>) => void };
};

const rate = (name: string, value: number): string => {
  const bounds = THRESHOLDS[name];
  if (!bounds) return "unknown";
  return value <= bounds[0] ? "good" : value <= bounds[1] ? "needs-improvement" : "poor";
};

const send = (metric: Metric, isRetry: boolean): void => {
  if (localStorage.getItem("umami.disabled") === "1") return;
  const umami = (window as UmamiWindow).umami;
  if (!umami?.track) {
    // TTFB/FCP can fire before the deferred tracker script has loaded.
    if (!isRetry) setTimeout(() => send(metric, true), 5000);
    return;
  }
  umami.track(`vital-${metric.name}`, {
    // CLS is a small unitless float (keep 3 decimals); the rest are ms.
    value:
      metric.name === "CLS"
        ? Math.round(metric.value * 1000) / 1000
        : Math.round(metric.value),
    rating: rate(metric.name, metric.value),
  });
};

const webVitalsToUmami = (metric: Metric): void => send(metric, false);

export default webVitalsToUmami;
